import { useContext } from 'react';
import AvailabilityError from '~/constants/availabilityError';
import UserVehicleContext, {
  useVehicleAvailability
} from '~/context/vdp/UserVehicleContext';
import { useGetVehicleDetails } from '~/context/vdp/VehicleContext';
import useGetIsVehiclePurchasedByUser from './useGetIsVehiclePurchasedByUser';
import useIsReservableVehicle from './useIsReservableVehicle';
import useIsWipVehicleReservedByUser from './useIsWipVehicleReservedByUser';

type AlertStatusParams = {
  alertToShow: AvailabilityError;
  hasError: boolean;
  isRcbPurchasingLoaded: boolean;
  isReservableAndPurchasePending: boolean;
  isSold: boolean;
  isWipReservedByUser: boolean;
  purchasePending: boolean;
  showPurchaseCompletedModal: boolean;
};

function getAlertStatus({
  hasError,
  alertToShow,
  isReservableAndPurchasePending,
  isWipReservedByUser,
  purchasePending
}: AlertStatusParams) {
  return (
    !hasError &&
    alertToShow !== AvailabilityError.IsSold &&
    alertToShow !== AvailabilityError.None &&
    alertToShow !== AvailabilityError.OnSitePrivate &&
    alertToShow !== AvailabilityError.Unavailable &&
    alertToShow !== AvailabilityError.UnavailableReserved &&
    !isReservableAndPurchasePending &&
    !isWipReservedByUser &&
    !purchasePending
  );
}

export default function useAlertStatus() {
  const { hasError } = useContext(UserVehicleContext).vehicleAvailability;
  const { error: alertToShow } = useVehicleAvailability();
  const { saleStatus } = useGetVehicleDetails();
  const isReservable = useIsReservableVehicle();
  const showPurchaseCompletedModal = useGetIsVehiclePurchasedByUser();
  const isWipReservedByUser = useIsWipVehicleReservedByUser();
  const {
    rcbPurchasing: { isLoaded: isRcbPurchasingLoaded }
  } = useContext(UserVehicleContext);

  const alertProperties = {
    isSold: alertToShow === AvailabilityError.IsSold || saleStatus === 'Sold',
    notAvailableInCA: alertToShow === AvailabilityError.NotAvailableInCA,
    outOfMarket: alertToShow === AvailabilityError.OutOfMarket,
    outOfMarketNewCar: alertToShow === AvailabilityError.OutOfMarketNewCar,
    purchasePending: alertToShow === AvailabilityError.PurchasePending,
    stopSaleRecall: alertToShow === AvailabilityError.StopSaleRecall,
    unavailable: alertToShow === AvailabilityError.Unavailable
  };

  // Handle cases where new errors are added, but we do not specifically handle them yet
  const otherUnavailable =
    alertToShow !== AvailabilityError.None &&
    !Object.values(alertProperties).some(Boolean);

  const { isSold, purchasePending } = alertProperties;

  const isReservableAndPurchasePending = isReservable && purchasePending;

  const params = {
    hasError,
    alertToShow,
    isReservableAndPurchasePending,
    isWipReservedByUser,
    purchasePending,
    isSold,
    isRcbPurchasingLoaded,
    showPurchaseCompletedModal
  };

  const showAlert = getAlertStatus(params);

  return { ...alertProperties, otherUnavailable, showAlert };
}
